/**
 * Desktop Offline Service for Tauri
 * Provides offline data persistence and sync capabilities using SQLite
 */

import { invoke } from '@tauri-apps/api/core';
import { config as productConfig } from '@/src/config';
import { isOffline, subscribeConnectivity } from './connectivity';
import {
  QueuedOperation,
  CacheEntry,
  SyncMetadata,
  SyncResult,
  SyncConflict,
  CacheStats,
  OfflineState,
  SyncStatus,
  OperationType,
  generateOperationId,
  calculateExpiresAt,
  isExpired,
  getEntityConfig,
  DEFAULT_RETRY_CONFIG,
  calculateRetryDelay,
} from './types';

// Database access goes through the encrypted (SQLCipher) Rust store via Tauri
// commands, NOT @tauri-apps/plugin-sql. This adapter keeps the same execute/select
// shape the rest of this file already uses.
interface DbAdapter {
  execute: (sql: string, params?: unknown[]) => Promise<{ rowsAffected: number }>;
  select: <T>(sql: string, params?: unknown[]) => Promise<T>;
}

function makeDbAdapter(): DbAdapter {
  return {
    execute: async (sql, params = []) => {
      const rowsAffected = await invoke<number>('secure_db_execute', { sql, params });
      return { rowsAffected };
    },
    select: <T>(sql: string, params: unknown[] = []) =>
      invoke<T>('secure_db_select', { sql, params }),
  };
}

// Resolve the signed-in identity used to open the per-user encrypted DB.
function readIdentity(): { userId: string; cboId: string } | null {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('user') : null;
    const userId = raw ? String(JSON.parse(raw)?.id ?? '') : '';
    const cboId =
      (typeof localStorage !== 'undefined' && localStorage.getItem('active_cbo_id')) || '';
    if (!userId) return null;
    return { userId, cboId: cboId || 'default' };
  } catch {
    return null;
  }
}

// Database instance (the encrypted-store adapter once opened).
let db: DbAdapter | null = null;
// The (user, org) the open store belongs to — the store is keyed by both, so a
// change means we must close it and open the right one.
let openIdentityKey: string | null = null;

// Sync state
let syncInProgress = false;
let syncTimer: ReturnType<typeof setInterval> | null = null;

// User-chosen "Work Offline" override (persisted in app_prefs).
let forcedOffline = false;

// Event listeners
type OfflineEventHandler = (state: OfflineState) => void;
const eventListeners: Set<OfflineEventHandler> = new Set();

// Handle for the verified-connectivity subscription.
let unsubscribeConnectivity: (() => void) | null = null;

// Verified, not `navigator.onLine`: that flag false-negatives in the desktop
// webview, and a false negative here stops sync and hands reads to the cache.
const networkOnline = (): boolean => !isOffline();
// Effective online: connected AND not manually forced offline.
const effectiveOnline = (): boolean => networkOnline() && !forcedOffline;

// Current state
let currentState: OfflineState = {
  isOnline: effectiveOnline(),
  forcedOffline: false,
  syncStatus: 'idle',
  lastSyncAt: null,
  pendingOperations: 0,
  conflicts: [],
  error: null,
};

/**
 * Initialize the offline database
 */
export async function initializeOfflineDb(): Promise<void> {
  if (db) return;

  try {
    // Open the per-user encrypted (SQLCipher) store. The Rust side creates the DB,
    // pulls the key from the OS keychain, and runs migrations.
    const identity = readIdentity();
    if (!identity) {
      throw new Error('Offline DB requires a signed-in user (no identity found)');
    }
    await invoke('secure_db_open', { userId: identity.userId, cboId: identity.cboId });
    db = makeDbAdapter();
    openIdentityKey = `${identity.userId}:${identity.cboId}`;

    // Restore the persisted "Work Offline" preference.
    try {
      const rows = await db.select<{ value: string }[]>(
        `SELECT value FROM app_prefs WHERE key = 'forced_offline'`
      );
      forcedOffline = rows[0]?.value === '1';
    } catch {
      forcedOffline = false;
    }
    updateState({ forcedOffline, isOnline: effectiveOnline() });

    // Set up online/offline listeners
    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      // An org switch changes which encrypted store this session belongs to.
      window.addEventListener('organizationChanged', handleIdentityChange);
    }
    unsubscribeConnectivity ??= subscribeConnectivity((offline) =>
      offline ? handleOffline() : handleOnline()
    );

    // Load initial state
    await refreshState();

    // Start background sync
    startBackgroundSync();

    console.log('Offline database initialized successfully');
  } catch (error) {
    console.error('Failed to initialize offline database:', error);
    throw error;
  }
}

/**
 * Close the database connection
 */
export async function closeOfflineDb(): Promise<void> {
  if (syncTimer) {
    clearInterval(syncTimer);
    syncTimer = null;
  }

  if (typeof window !== 'undefined') {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('organizationChanged', handleIdentityChange);
  }
  unsubscribeConnectivity?.();
  unsubscribeConnectivity = null;

  if (db) {
    await invoke('secure_db_close').catch(() => {});
    db = null;
    openIdentityKey = null;
  }
}

/**
 * Close the open store and open the one belonging to the current (user, org).
 *
 * The encrypted store is opened per user AND per org, but it used to be opened
 * exactly once per app launch — so after a super_admin switched organizations,
 * everything cached for the new org was written into (and read back out of) the
 * previous org's database. Cache keys carry the identity too; this keeps the
 * on-disk boundary honest as well.
 */
export async function reopenIfIdentityChanged(): Promise<void> {
  const identity = readIdentity();
  if (!identity) return;

  const key = `${identity.userId}:${identity.cboId}`;
  if (!db || key === openIdentityKey) return;

  await invoke('secure_db_close').catch(() => {});
  db = null;
  openIdentityKey = null;
  await initializeOfflineDb().catch((e) => {
    console.error('Failed to reopen offline DB after identity change:', e);
  });
}

function handleIdentityChange(): void {
  void reopenIfIdentityChanged();
}

/**
 * Wipe the encrypted offline DB + its keychain key (call on logout / account
 * switch). PHI on the device becomes unrecoverable.
 */
export async function wipeOfflineDb(): Promise<void> {
  const identity = readIdentity();
  if (syncTimer) {
    clearInterval(syncTimer);
    syncTimer = null;
  }
  db = null;
  openIdentityKey = null;
  if (identity) {
    await invoke('secure_db_wipe', {
      userId: identity.userId,
      cboId: identity.cboId,
    }).catch(() => {});
  }
}

// ============================================================================
// Cache Operations
// ============================================================================

/**
 * Cache an entity
 */
export async function cacheEntity<T>(
  entityType: string,
  entityId: string,
  data: T,
  ttlSeconds?: number
): Promise<void> {
  if (!db) throw new Error('Database not initialized');

  const config = getEntityConfig(entityType);
  const ttl = ttlSeconds ?? config?.ttlSeconds ?? 3600;
  const now = Date.now();
  const expiresAt = calculateExpiresAt(ttl);

  // Data minimization: when an allowlist is configured, store ONLY those fields.
  const stored = minimizeForCache(entityType, data);

  await db.execute(
    `INSERT OR REPLACE INTO cached_entities
     (entity_type, entity_id, data, cached_at, expires_at, version)
     VALUES (?, ?, ?, ?, ?, ?)`,
    [entityType, entityId, JSON.stringify(stored), now, expiresAt, null]
  );

  // LRU cap: keep at most maxCacheSize most-recent rows per entity type.
  if (config?.maxCacheSize && config.maxCacheSize > 0) {
    await db.execute(
      `DELETE FROM cached_entities
       WHERE entity_type = ?
         AND entity_id NOT IN (
           SELECT entity_id FROM cached_entities
           WHERE entity_type = ? ORDER BY cached_at DESC LIMIT ?
         )`,
      [entityType, entityType, config.maxCacheSize]
    );
  }
}

/** Apply the per-entity field allowlist (data minimization) before caching. */
function minimizeForCache<T>(entityType: string, data: T): unknown {
  const allow = getEntityConfig(entityType)?.cacheFields;
  if (!allow || allow.length === 0 || typeof data !== 'object' || data === null) {
    return data;
  }
  const src = data as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of allow) {
    if (key in src) out[key] = src[key];
  }
  return out;
}

/**
 * Get a cached entity
 */
export async function getCachedEntity<T>(
  entityType: string,
  entityId: string
): Promise<T | null> {
  if (!db) throw new Error('Database not initialized');

  const results = await db.select<CacheEntry<string>[]>(
    `SELECT * FROM cached_entities WHERE entity_type = ? AND entity_id = ?`,
    [entityType, entityId]
  );

  if (results.length === 0) return null;

  const entry = results[0];

  // Check expiration
  if (isExpired(entry.expiresAt)) {
    await removeCachedEntity(entityType, entityId);
    return null;
  }

  return JSON.parse(entry.data) as T;
}

/**
 * Get all cached entities of a type
 */
export async function getCachedEntitiesByType<T>(
  entityType: string
): Promise<Array<{ entityId: string; data: T }>> {
  if (!db) throw new Error('Database not initialized');

  const now = Date.now();
  const results = await db.select<CacheEntry<string>[]>(
    `SELECT * FROM cached_entities WHERE entity_type = ? AND expires_at > ?`,
    [entityType, now]
  );

  return results.map((entry) => ({
    entityId: entry.entityId,
    data: JSON.parse(entry.data) as T,
  }));
}

/**
 * Remove a cached entity
 */
export async function removeCachedEntity(
  entityType: string,
  entityId: string
): Promise<void> {
  if (!db) throw new Error('Database not initialized');

  await db.execute(
    `DELETE FROM cached_entities WHERE entity_type = ? AND entity_id = ?`,
    [entityType, entityId]
  );
}

/**
 * Invalidate cache for an entity type
 */
export async function invalidateCache(
  entityType: string,
  entityId?: string
): Promise<void> {
  if (!db) throw new Error('Database not initialized');

  if (entityId) {
    await removeCachedEntity(entityType, entityId);
  } else {
    await db.execute(`DELETE FROM cached_entities WHERE entity_type = ?`, [
      entityType,
    ]);
  }
}

/**
 * Clear all expired cache entries
 */
export async function clearExpiredCache(): Promise<number> {
  if (!db) throw new Error('Database not initialized');

  const now = Date.now();
  const result = await db.execute(
    `DELETE FROM cached_entities WHERE expires_at < ?`,
    [now]
  );

  return result.rowsAffected;
}

/**
 * Clear all cache
 */
export async function clearAllCache(): Promise<void> {
  if (!db) throw new Error('Database not initialized');

  await db.execute(`DELETE FROM cached_entities`);
}

/**
 * Get cache statistics
 */
export async function getCacheStats(): Promise<CacheStats> {
  if (!db) throw new Error('Database not initialized');

  const now = Date.now();

  const countResult = await db.select<[{ count: number }]>(
    `SELECT COUNT(*) as count FROM cached_entities`
  );

  const sizeResult = await db.select<[{ total_size: number }]>(
    `SELECT SUM(LENGTH(data)) as total_size FROM cached_entities`
  );

  const oldestResult = await db.select<[{ oldest: number | null }]>(
    `SELECT MIN(cached_at) as oldest FROM cached_entities`
  );

  const newestResult = await db.select<[{ newest: number | null }]>(
    `SELECT MAX(cached_at) as newest FROM cached_entities`
  );

  const expiredResult = await db.select<[{ expired: number }]>(
    `SELECT COUNT(*) as expired FROM cached_entities WHERE expires_at < ?`,
    [now]
  );

  return {
    totalEntries: countResult[0]?.count ?? 0,
    totalSize: sizeResult[0]?.total_size ?? 0,
    oldestEntry: oldestResult[0]?.oldest ?? null,
    newestEntry: newestResult[0]?.newest ?? null,
    expiredCount: expiredResult[0]?.expired ?? 0,
  };
}

// ============================================================================
// Operation Queue
// ============================================================================

/**
 * Queue an operation for sync
 */
export async function queueOperation<T>(
  type: OperationType,
  entityType: string,
  entityId: string | undefined,
  payload: T
): Promise<string> {
  if (!db) throw new Error('Database not initialized');

  const id = generateOperationId();
  const now = Date.now();

  await db.execute(
    `INSERT INTO operation_queue
     (id, operation_type, entity_type, entity_id, payload, timestamp, retry_count, max_retries, status, created_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      id,
      type,
      entityType,
      entityId ?? null,
      JSON.stringify(payload),
      now,
      0,
      DEFAULT_RETRY_CONFIG.maxRetries,
      'pending',
      now,
    ]
  );

  await refreshState();
  return id;
}

/**
 * Get pending operations
 */
export async function getPendingOperations(): Promise<QueuedOperation[]> {
  if (!db) throw new Error('Database not initialized');

  const results = await db.select<QueuedOperation[]>(
    `SELECT * FROM operation_queue WHERE status = 'pending' ORDER BY created_at ASC`
  );

  return results.map((op) => ({
    ...op,
    payload: JSON.parse(op.payload as unknown as string),
  }));
}

/**
 * Get all operations (including failed)
 */
export async function getAllOperations(): Promise<QueuedOperation[]> {
  if (!db) throw new Error('Database not initialized');

  const results = await db.select<QueuedOperation[]>(
    `SELECT * FROM operation_queue ORDER BY created_at ASC`
  );

  return results.map((op) => ({
    ...op,
    payload: JSON.parse(op.payload as unknown as string),
  }));
}

/**
 * Update operation status
 */
export async function updateOperationStatus(
  operationId: string,
  status: 'pending' | 'syncing' | 'failed' | 'completed',
  errorMessage?: string
): Promise<void> {
  if (!db) throw new Error('Database not initialized');

  const now = Date.now();

  if (status === 'failed') {
    await db.execute(
      `UPDATE operation_queue
       SET status = ?, error_message = ?, last_attempt_at = ?, retry_count = retry_count + 1
       WHERE id = ?`,
      [status, errorMessage ?? null, now, operationId]
    );
  } else {
    await db.execute(
      `UPDATE operation_queue SET status = ?, last_attempt_at = ? WHERE id = ?`,
      [status, now, operationId]
    );
  }

  await refreshState();
}

/**
 * Remove completed operations
 */
export async function clearCompletedOperations(): Promise<number> {
  if (!db) throw new Error('Database not initialized');

  const result = await db.execute(
    `DELETE FROM operation_queue WHERE status = 'completed'`
  );

  await refreshState();
  return result.rowsAffected;
}

/**
 * Retry failed operations
 */
export async function retryFailedOperations(): Promise<void> {
  if (!db) throw new Error('Database not initialized');

  await db.execute(
    `UPDATE operation_queue
     SET status = 'pending', error_message = NULL
     WHERE status = 'failed' AND retry_count < max_retries`
  );

  await refreshState();
}

// ============================================================================
// Sync Metadata
// ============================================================================

/**
 * Get sync metadata for an entity type
 */
export async function getSyncMetadata(
  entityType: string
): Promise<SyncMetadata | null> {
  if (!db) throw new Error('Database not initialized');

  const results = await db.select<SyncMetadata[]>(
    `SELECT * FROM sync_metadata WHERE entity_type = ?`,
    [entityType]
  );

  return results[0] ?? null;
}

/**
 * Update sync metadata
 */
export async function updateSyncMetadata(
  entityType: string,
  serverVersion?: string
): Promise<void> {
  if (!db) throw new Error('Database not initialized');

  const now = Date.now();

  await db.execute(
    `INSERT OR REPLACE INTO sync_metadata (entity_type, last_synced_at, server_version)
     VALUES (?, ?, ?)`,
    [entityType, now, serverVersion ?? null]
  );
}

// ============================================================================
// Sync Conflicts
// ============================================================================

/**
 * Add a sync conflict
 */
export async function addSyncConflict<T>(
  entityType: string,
  entityId: string,
  clientData: T,
  serverData: T,
  clientTimestamp: number,
  serverTimestamp: number
): Promise<string> {
  if (!db) throw new Error('Database not initialized');

  const id = generateOperationId();
  const now = Date.now();

  await db.execute(
    `INSERT INTO sync_conflicts
     (id, entity_type, entity_id, client_data, server_data, client_timestamp, server_timestamp, created_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      id,
      entityType,
      entityId,
      JSON.stringify(clientData),
      JSON.stringify(serverData),
      clientTimestamp,
      serverTimestamp,
      now,
    ]
  );

  await refreshState();
  return id;
}

/**
 * Get all unresolved conflicts
 */
export async function getConflicts(): Promise<SyncConflict[]> {
  if (!db) throw new Error('Database not initialized');

  const results = await db.select<SyncConflict[]>(
    `SELECT * FROM sync_conflicts ORDER BY created_at ASC`
  );

  return results.map((conflict) => ({
    ...conflict,
    clientData: JSON.parse(conflict.clientData as unknown as string),
    serverData: JSON.parse(conflict.serverData as unknown as string),
    resolved: false,
  }));
}

/**
 * Resolve a conflict
 */
export async function resolveConflict(
  conflictId: string,
  resolution: 'client' | 'server' | 'merged',
  mergedData?: unknown
): Promise<void> {
  if (!db) throw new Error('Database not initialized');

  // Get the conflict
  const conflicts = await db.select<SyncConflict[]>(
    `SELECT * FROM sync_conflicts WHERE id = ?`,
    [conflictId]
  );

  if (conflicts.length === 0) {
    throw new Error('Conflict not found');
  }

  const conflict = conflicts[0];

  // Determine final data
  let finalData: unknown;
  if (resolution === 'client') {
    finalData = JSON.parse(conflict.clientData as unknown as string);
  } else if (resolution === 'server') {
    finalData = JSON.parse(conflict.serverData as unknown as string);
  } else {
    finalData = mergedData;
  }

  // Queue an update operation with the resolved data
  if (finalData) {
    await queueOperation(
      'UPDATE',
      conflict.entityType,
      conflict.entityId,
      finalData
    );
  }

  // Remove the conflict
  await db.execute(`DELETE FROM sync_conflicts WHERE id = ?`, [conflictId]);

  await refreshState();
}

// ============================================================================
// Sync Operations
// ============================================================================

/**
 * Perform a sync
 */
export async function syncNow(): Promise<SyncResult> {
  if (syncInProgress) {
    return {
      succeeded: 0,
      failed: 0,
      conflicts: [],
      timestamp: Date.now(),
    };
  }

  if (!currentState.isOnline) {
    return {
      succeeded: 0,
      failed: 0,
      conflicts: [],
      timestamp: Date.now(),
    };
  }

  syncInProgress = true;
  updateState({ syncStatus: 'syncing' });

  const result: SyncResult = {
    succeeded: 0,
    failed: 0,
    conflicts: [],
    timestamp: Date.now(),
  };

  try {
    const operations = await getPendingOperations();

    for (const operation of operations) {
      try {
        await updateOperationStatus(operation.id, 'syncing');

        // Execute the operation against the API
        await executeOperation(operation);

        await updateOperationStatus(operation.id, 'completed');
        result.succeeded++;
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';

        // Check if it's a conflict
        if (
          error instanceof Error &&
          (error as Error & { code?: string }).code === 'CONFLICT'
        ) {
          const conflictError = error as Error & {
            serverData?: unknown;
          };
          const conflictId = await addSyncConflict(
            operation.entityType,
            operation.entityId ?? '',
            operation.payload,
            conflictError.serverData,
            operation.timestamp,
            Date.now()
          );

          result.conflicts.push({
            id: conflictId,
            entityType: operation.entityType,
            entityId: operation.entityId ?? '',
            clientData: operation.payload,
            serverData: conflictError.serverData,
            clientTimestamp: operation.timestamp,
            serverTimestamp: Date.now(),
            resolved: false,
          });

          await updateOperationStatus(operation.id, 'failed', 'Conflict');
        } else {
          await updateOperationStatus(operation.id, 'failed', errorMessage);
        }
        result.failed++;
      }
    }

    updateState({
      syncStatus: 'idle',
      lastSyncAt: Date.now(),
      error: null,
    });
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Sync failed';
    updateState({
      syncStatus: 'error',
      error: errorMessage,
    });
  } finally {
    syncInProgress = false;
  }

  return result;
}

/**
 * Execute a single operation against the API
 */
async function executeOperation(operation: QueuedOperation): Promise<void> {
  // This would be implemented to call the actual API
  // For now, we'll use the fetch API with the appropriate endpoint
  const baseUrl =
    productConfig.weaveinApi.baseUrl ?? 'https://weavein-api.onrender.com';

  const endpoint = `${baseUrl}/api/sync/push`;

  // Route through authFetch so auth (now keychain-backed) + X-CBO-ID are applied
  // centrally — no direct localStorage token read here.
  const { authFetch } = await import('@/lib/authFetch');
  const response = await authFetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      operations: [operation],
      clientTimestamp: Date.now(),
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    if (response.status === 409) {
      const error = new Error('Conflict detected') as Error & {
        code: string;
        serverData: unknown;
      };
      error.code = 'CONFLICT';
      error.serverData = errorData.serverData;
      throw error;
    }

    throw new Error(errorData.message || `HTTP ${response.status}`);
  }
}

// ============================================================================
// Background Sync
// ============================================================================

/**
 * Start background sync timer
 */
function startBackgroundSync(): void {
  if (syncTimer) return;

  // Sync every 30 seconds when online; also evict expired cache each tick so PHI
  // doesn't linger past its TTL on disk.
  syncTimer = setInterval(async () => {
    // Not every writer of active_cbo_id dispatches `organizationChanged`.
    await reopenIfIdentityChanged().catch(() => {});
    await clearExpiredCache().catch(() => {});
    if (currentState.isOnline && !syncInProgress) {
      const pendingOps = await getPendingOperations();
      if (pendingOps.length > 0) {
        await syncNow();
      }
    }
  }, 30000);
}

/**
 * Stop background sync timer
 */
function stopBackgroundSync(): void {
  if (syncTimer) {
    clearInterval(syncTimer);
    syncTimer = null;
  }
}

// ============================================================================
// State Management
// ============================================================================

function handleOnline(): void {
  const online = effectiveOnline();
  updateState({ isOnline: online, syncStatus: online ? 'idle' : 'offline' });
  // Trigger sync when actually (effectively) back online.
  if (online) syncNow();
}

function handleOffline(): void {
  updateState({ isOnline: false, syncStatus: 'offline' });
}

/**
 * Manually force offline ("Work Offline") regardless of connectivity. Persisted
 * so it survives restarts. Turning it off while connected drains the queue.
 */
export async function setForcedOffline(value: boolean): Promise<void> {
  forcedOffline = value;
  if (db) {
    try {
      await db.execute(
        `INSERT OR REPLACE INTO app_prefs (key, value) VALUES ('forced_offline', ?)`,
        [value ? '1' : '0']
      );
    } catch (e) {
      console.error('Failed to persist forced-offline pref');
    }
  }
  const online = effectiveOnline();
  updateState({ forcedOffline: value, isOnline: online, syncStatus: online ? 'idle' : 'offline' });
  if (online && !syncInProgress) void syncNow();
}

export function getForcedOffline(): boolean {
  return forcedOffline;
}

async function refreshState(): Promise<void> {
  if (!db) return;

  try {
    const pendingOps = await db.select<[{ count: number }]>(
      `SELECT COUNT(*) as count FROM operation_queue WHERE status = 'pending'`
    );

    const conflicts = await getConflicts();

    updateState({
      pendingOperations: pendingOps[0]?.count ?? 0,
      conflicts,
    });
  } catch (error) {
    console.error('Failed to refresh state:', error);
  }
}

function updateState(updates: Partial<OfflineState>): void {
  currentState = { ...currentState, ...updates };
  notifyListeners();
}

function notifyListeners(): void {
  eventListeners.forEach((listener) => listener(currentState));
}

// ============================================================================
// Event Subscription
// ============================================================================

/**
 * Subscribe to offline state changes
 */
export function subscribeToOfflineState(
  handler: OfflineEventHandler
): () => void {
  eventListeners.add(handler);

  // Immediately notify with current state
  handler(currentState);

  return () => {
    eventListeners.delete(handler);
  };
}

/**
 * Get current offline state
 */
export function getOfflineState(): OfflineState {
  return { ...currentState };
}

// ============================================================================
// Fetch with Offline Support
// ============================================================================

/**
 * Fetch data with offline caching support
 */
export async function fetchWithOfflineSupport<T>(
  entityType: string,
  entityId: string,
  fetchFn: () => Promise<T>,
  options?: {
    ttlSeconds?: number;
    forceRefresh?: boolean;
  }
): Promise<T> {
  const { ttlSeconds, forceRefresh = false } = options ?? {};

  // If online and not forcing refresh, try cache first
  if (currentState.isOnline && !forceRefresh) {
    const cached = await getCachedEntity<T>(entityType, entityId);
    if (cached) return cached;
  }

  // If online, try to fetch
  if (currentState.isOnline) {
    try {
      const data = await fetchFn();
      await cacheEntity(entityType, entityId, data, ttlSeconds);
      return data;
    } catch (error) {
      // Fall through to cache
      console.warn('Fetch failed, trying cache:', error);
    }
  }

  // Try cache
  const cached = await getCachedEntity<T>(entityType, entityId);
  if (cached) return cached;

  throw new Error('No data available offline');
}

/**
 * Mutate with offline queue support
 */
export async function mutateWithOfflineSupport<T, R>(
  type: OperationType,
  entityType: string,
  entityId: string | undefined,
  payload: T,
  mutateFn: (payload: T) => Promise<R>
): Promise<R | { queued: true; operationId: string }> {
  if (currentState.isOnline) {
    try {
      const result = await mutateFn(payload);

      // Update cache with new data
      if (entityId && result) {
        await cacheEntity(entityType, entityId, result);
      }

      return result;
    } catch (error) {
      // If mutation fails while online, still queue it
      console.warn('Mutation failed, queueing for retry:', error);
    }
  }

  // Queue for offline sync
  const operationId = await queueOperation(type, entityType, entityId, payload);

  // Apply optimistic update to cache
  if (entityId && type !== 'DELETE') {
    await cacheEntity(entityType, entityId, payload);
  } else if (entityId && type === 'DELETE') {
    await removeCachedEntity(entityType, entityId);
  }

  return { queued: true, operationId };
}

// ============================================================================
// Export default service object
// ============================================================================

export const desktopOfflineService = {
  initialize: initializeOfflineDb,
  close: closeOfflineDb,

  // Cache
  cache: cacheEntity,
  getCached: getCachedEntity,
  getCachedByType: getCachedEntitiesByType,
  removeCache: removeCachedEntity,
  invalidateCache,
  clearExpiredCache,
  clearAllCache,
  getCacheStats,

  // Queue
  queueOperation,
  getPendingOperations,
  getAllOperations,
  updateOperationStatus,
  clearCompletedOperations,
  retryFailedOperations,

  // Sync
  getSyncMetadata,
  updateSyncMetadata,
  syncNow,

  // Conflicts
  getConflicts,
  resolveConflict,

  // State
  subscribe: subscribeToOfflineState,
  getState: getOfflineState,
  setForcedOffline,
  getForcedOffline,

  // Lifecycle
  wipe: wipeOfflineDb,

  // Helpers
  fetchWithOfflineSupport,
  mutateWithOfflineSupport,
};

export default desktopOfflineService;
